import httpService from '@Utils/httpService';
import { ForgeLevel } from '@/scripts/js/forgeModels';

const baseUrl = 'api/levels';

export default {
  /**
   * Get all levels for a specific asset (raw response)
   * @param {string} assetId - The asset ID
   * @returns {Promise} - Array of level objects
   */
  getAllAssetLevels(assetId) {
    return httpService.get({
      url: baseUrl,
      params: { assetId },
    });
  },

  /**
   * Get all levels for a specific asset, parsed into ForgeLevel objects
   * @param {string} assetId - The asset ID
   * @returns {Promise<ForgeLevel[]>} - Array of parsed ForgeLevel objects
   */
  async fetchAssetLevelsParsed(assetId, config = {}) {
    const response = await httpService.get({
      url: baseUrl,
      params: { assetId },
      ...config,
    });
    const levels = response.data ?? [];
    return ForgeLevel.parseLevelsFromDB(levels);
  },

  /**
   * Upload a level plan image/PDF
   * @param {FormData} formData - FormData with fileToUpload, assetId, levelId, oldUrn, oldImage
   * @returns {Promise} - Response with planUrn and planImageUrl
   */
  uploadLevelPlan(formData) {
    return httpService.post({
      url: `${baseUrl}/plan`,
      data: formData,
      headers: {
        'Content-Type': false,
        processData: false,
      },
    });
  },

  /**
   * Create a new level
   * @param {string} assetId - The asset ID
   * @param {Object} level - The level object to create
   * @returns {Promise} - The created level ID
   */
  createLevel(assetId, level) {
    return httpService.post({
      url: baseUrl,
      data: { assetId, level },
    });
  },

  /**
   * Delete a level
   * @param {string} id - The level ID to delete
   * @param {string} assetId - The asset ID
   * @returns {Promise}
   */
  deleteLevel(id, assetId) {
    return httpService.delete({
      url: `${baseUrl}/${id}`,
      params: { assetId },
    });
  },

  /**
   * Edit multiple levels at once (bulk update)
   * @param {string} assetId - The asset ID
   * @param {Array} levels - Array of level objects to update
   * @returns {Promise}
   */
  editLevels(assetId, levels) {
    return httpService.put({
      url: `${baseUrl}/bulk`,
      data: { assetId, levels },
    });
  },

  /**
   * Edit a single level
   * @param {string} assetId - The asset ID
   * @param {Object} level - The level object to update
   * @returns {Promise}
   */
  editLevel(assetId, level) {
    return httpService.put({
      url: baseUrl,
      data: { assetId, level },
    });
  },
};
